/**
 * DNS auto-fix diff preview mockup. The visitor sees the exact change we'd
 * make to their TXT record — old vs new — and can click "Apply" to play
 * through a 3-step "applied → verifying → live" animation. The interaction
 * makes the auto-fix promise concrete in 5 seconds.
 */
import { useEffect, useState } from 'react';
import { Check, GitBranch, Loader2 } from 'lucide-react';

type State = 'idle' | 'applying' | 'verifying' | 'done';

export function DnsAutofixMockup() {
  const [state, setState] = useState<State>('idle');

  useEffect(() => {
    if (state === 'applying') {
      const t = window.setTimeout(() => setState('verifying'), 1100);
      return () => window.clearTimeout(t);
    }
    if (state === 'verifying') {
      const t = window.setTimeout(() => setState('done'), 1400);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [state]);

  return (
    <div className="overflow-hidden rounded-lg border bg-background/80 shadow-lg ring-1 ring-border/40">
      <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-3 text-xs font-mono uppercase tracking-wider text-muted-foreground">
        <span className="flex items-center gap-2">
          <GitBranch className="h-4 w-4" />
          cloudflare · _dmarc.acme.com TXT
        </span>
        <span
          className={
            'rounded px-2 py-0.5 ' +
            (state === 'done'
              ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              : state === 'idle'
                ? 'bg-muted text-muted-foreground'
                : 'bg-amber-500/10 text-amber-700 dark:text-amber-400')
          }
        >
          {state === 'idle' && 'preview'}
          {state === 'applying' && 'applying…'}
          {state === 'verifying' && 'verifying…'}
          {state === 'done' && 'live'}
        </span>
      </div>
      <div className="p-5 font-mono text-sm">
        <div className="rounded-md border-l-4 border-rose-400 bg-rose-500/5 px-3 py-2">
          <span className="font-bold text-rose-700 dark:text-rose-400">−</span>{' '}
          <span className="text-muted-foreground">v=DMARC1; p=none; rua=mailto:dmarc@acme.com</span>
        </div>
        <div className="mt-2 rounded-md border-l-4 border-emerald-400 bg-emerald-500/5 px-3 py-2 leading-relaxed">
          <span className="font-bold text-emerald-700 dark:text-emerald-400">+</span>{' '}
          <span className="break-all">
            v=DMARC1;{' '}
            <strong className="text-emerald-700 dark:text-emerald-400">p=quarantine</strong>;{' '}
            <strong className="text-emerald-700 dark:text-emerald-400">pct=25</strong>;{' '}
            rua=mailto:rua@inboxguard.io,mailto:dmarc@acme.com
          </span>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2 text-xs uppercase tracking-wider">
          <Step label="Apply at registrar" active={state === 'applying'} done={state === 'verifying' || state === 'done'} />
          <Step label="Re-verify" active={state === 'verifying'} done={state === 'done'} />
          <Step label="Live + monitored" active={false} done={state === 'done'} />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          {/* All caption states share one grid cell, so the row always reserves
              the tallest state's height and the card never resizes between
              states (which used to shove the page content up and down). */}
          <span className="grid flex-1 text-xs text-muted-foreground">
            <span className={'col-start-1 row-start-1 ' + (state === 'idle' ? '' : 'invisible')}>
              Diff vs. live record. Every change is logged with the prior value, so you can roll back in one click.
            </span>
            <span className={'col-start-1 row-start-1 inline-flex items-center gap-1.5 ' + (state === 'applying' ? '' : 'invisible')}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Pushing to Cloudflare API…
            </span>
            <span className={'col-start-1 row-start-1 inline-flex items-center gap-1.5 ' + (state === 'verifying' ? '' : 'invisible')}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Re-querying authoritative resolvers…
            </span>
            <span className={'col-start-1 row-start-1 inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 ' + (state === 'done' ? '' : 'invisible')}>
              <Check className="h-3.5 w-3.5" /> Verified live in 1.3s. Quarantine is ramping at 25%, and we&apos;ll raise it to 100% in 14 days if your reports stay clean.
            </span>
          </span>
          {/* Fixed-width slot so the caption's width — and therefore its
              wrapped height — stays constant whether or not a button shows. */}
          <div className="flex min-w-[132px] justify-end">
            {state === 'idle' && (
              <button
                type="button"
                onClick={() => setState('applying')}
                className="rounded-md bg-primary px-4 py-2 text-xs font-semibold uppercase tracking-wider text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                Apply change
              </button>
            )}
            {state === 'done' && (
              <button
                type="button"
                onClick={() => setState('idle')}
                className="rounded-md border px-4 py-2 text-xs font-medium hover:bg-muted"
              >
                Reset demo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ label, active, done }: { label: string; active: boolean; done: boolean }) {
  return (
    <div className="flex items-center gap-2 rounded-md border bg-card/50 px-3 py-2">
      <span
        className={
          'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold ' +
          (done
            ? 'bg-emerald-500 text-white'
            : active
              ? 'bg-primary text-primary-foreground animate-pulse'
              : 'bg-muted text-muted-foreground')
        }
      >
        {done ? '✓' : active ? '•' : ''}
      </span>
      <span className={done || active ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    </div>
  );
}
